import {
  faCheck,
  faEdit,
  faEye,
  faInfoCircle,
  faPlus,
  faShieldAlt,
  faSpinner,
  faTimes,
  faUniversity
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Badge, Button, Card, Modal } from "@mantine/core";
import React, { useState } from "react";
import { connect } from "react-redux";
import ABAApi from "../../data/api/abaApi";
import { IState } from "../../data/reducers/state";
import { PersonBankDetails } from "../../types/aba";
import { IPerson } from "../../types/persons/person";
import { PersonBankDetailsForm } from "../aba";
import ErrorBoundary from "../common/ErrorBoundary";
import { CenteredLoader } from "../common/loader";

interface PersonalBankDetailsSettingsProps {
  // Redux props
  self: IPerson;
}

/**
 * Personal bank details settings component for user settings page
 * Shows readonly view of bank details and export history
 */
const PersonalBankDetailsSettings: React.FunctionComponent<PersonalBankDetailsSettingsProps> = ({
  self,
}) => {
  const [bankDetails, setBankDetails] = useState<PersonBankDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingBankDetails, setLoadingBankDetails] = useState(false);
  const [hasRequestedBankDetails, setHasRequestedBankDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);



  const loadBankDetails = async () => {
    if (!self?._id) return;

    setLoadingBankDetails(true);
    setError(null);

    try {
      // Get the first association ID for personal settings
      const associationId = self.associations && self.associations.length > 0
        ? self.associations[0].associationId
        : '';

      if (!associationId) {
        setError("No association found for loading bank details");
        return;
      }
      const bankDetailsResult = await ABAApi.getPersonBankDetails(self._id, associationId, true);
      setBankDetails(bankDetailsResult);
      setHasRequestedBankDetails(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load bank details");
      console.warn("Failed to load bank details:", err);
    } finally {
      setLoadingBankDetails(false);
    }
  };

  const handleBankDetailsSaved = (details: PersonBankDetails) => {
    setBankDetails(details);
    setShowAddForm(false);
    setShowEditForm(false);
    setError(null);
  };

  const handleAddBankDetails = () => {
    setShowAddForm(true);
    setError(null);
  };

  const handleEditBankDetails = () => {
    setShowEditForm(true);
    setError(null);
  };

  const handleRequestBankDetails = () => {
    loadBankDetails();
  };

  const handleCloseForm = () => {
    setShowAddForm(false);
    setShowEditForm(false);
  };

  if (loading) {
    return <CenteredLoader />;
  }

  return (
    <div>
      <h4>Payment Details</h4>
      <p className="text-muted">
        View your bank details for electronic payments and payment history.
        Contact an administrator if you need to update your bank information.
      </p>

      {error && (
        <Alert color="red" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Bank Details Card */}
      <Card className="mb-4">
        <Card.Section withBorder className="d-flex justify-content-between align-items-center p-3">
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faUniversity} className="me-2" />
            Your Bank Details
          </h5>
          {bankDetails && hasRequestedBankDetails && !showEditForm && (
            <Button color="blue" variant="outline" size="sm" onClick={handleEditBankDetails}>
              <FontAwesomeIcon icon={faEdit} className="me-1" />
              Edit
            </Button>
          )}
        </Card.Section>
        <div style={{ padding: '1rem' }}>
          {!hasRequestedBankDetails ? (
            <Alert color="blue">
              <h6>
                <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                Bank Details Protected
              </h6>
              <p className="mb-3">
                For security reasons, your bank details are not displayed automatically.
                Click below to securely view your payment information.
              </p>
              <Button
                color="blue"
                onClick={handleRequestBankDetails}
                disabled={loadingBankDetails}
              >
                {loadingBankDetails ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    Loading...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faEye} className="me-2" />
                    View Bank Details
                  </>
                )}
              </Button>
            </Alert>
          ) : bankDetails ? (
            <div>
              <div className="row">
                <div className="col-md-6">
                  <dl className="row">
                    <dt className="col-sm-4">BSB:</dt>
                    <dd className="col-sm-8">
                      <code>{bankDetails.bsb}</code>
                    </dd>

                    <dt className="col-sm-4">Account:</dt>
                    <dd className="col-sm-8">
                      {bankDetails.accountNumber ? (
                        <span>
                          {bankDetails.accountNumber}
                          {bankDetails.bankName && (
                            <span className="text-muted ms-2">({bankDetails.bankName})</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted">Not available</span>
                      )}
                    </dd>

                    <dt className="col-sm-4">Account Name:</dt>
                    <dd className="col-sm-8">{bankDetails.accountName}</dd>
                  </dl>
                </div>

                <div className="col-md-6">
                  <dl className="row">
                    <dt className="col-sm-4">Status:</dt>
                    <dd className="col-sm-8">
                      {bankDetails.isValid ? (
                        <Badge color="green">
                          <FontAwesomeIcon icon={faCheck} className="me-1" />
                          Valid
                        </Badge>
                      ) : (
                        <Badge color="red">
                          <FontAwesomeIcon icon={faTimes} className="me-1" />
                          Invalid
                        </Badge>
                      )}
                    </dd>

                    {bankDetails.lastValidated && (
                      <>
                        <dt className="col-sm-4">Last Validated:</dt>
                        <dd className="col-sm-8">
                          {new Date(bankDetails.lastValidated).toLocaleDateString()}
                        </dd>
                      </>
                    )}
                  </dl>
                </div>
              </div>

              {!bankDetails.isValid && (
                <Alert color="yellow" className="mt-3">
                  <h6>Invalid Bank Details</h6>
                  <p className="mb-0">
                    Your bank details are invalid and you won't receive electronic payments.
                    You can update your bank details here, or please contact an administrator.
                  </p>
                </Alert>
              )}
            </div>
          ) : hasRequestedBankDetails && !showAddForm ? (
            <Alert color="blue">
              <h6>No Bank Details</h6>
              <p>
                You don't have bank details on file. Add your bank information
                to receive electronic payments.
              </p>
              <Button color="blue" onClick={handleAddBankDetails}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Bank Details
              </Button>
            </Alert>
          ) : null}
        </div>
      </Card>

      {/* Add Bank Details Form */}
      {showAddForm && self && (
        <ErrorBoundary>
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Add Your Bank Details</h5>
              <Button color="gray" variant="outline" size="sm" onClick={handleCloseForm}>
                <FontAwesomeIcon icon={faTimes} className="me-1" />
                Cancel
              </Button>
            </div>
            <PersonBankDetailsForm
              personId={self._id}
              associationId={self.associations && self.associations.length > 0 ? self.associations[0].associationId : ''}
              personName={self.preferredName || self.firstName}
              isModal={false}
              onSave={handleBankDetailsSaved}
              isSelf={true}
            />
          </div>
        </ErrorBoundary>
      )}

      {/* Edit Bank Details Modal */}
      <Modal
        opened={showEditForm && !!self && !!bankDetails}
        onClose={handleCloseForm}
        size="lg"
        title={
          <div>
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Edit Your Bank Details
          </div>
        }
      >
        <ErrorBoundary>
          {self && bankDetails && (
            <PersonBankDetailsForm
              personId={self._id}
              associationId={self.associations && self.associations.length > 0 ? self.associations[0].associationId : ''}
              personName={self.preferredName || self.firstName}
              existingDetails={bankDetails}
              isModal={false}
              onSave={handleBankDetailsSaved}
              onClose={handleCloseForm}
              isSelf={true}
            />
          )}
        </ErrorBoundary>
      </Modal>


      {/* Information Card */}
      <Alert color="blue" className="mt-4">
        <h6>
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
          About Electronic Payments
        </h6>
        <ul className="mb-0">
          <li><strong>Security:</strong> Your full account number is never displayed or stored insecurely</li>
          <li><strong>Validation:</strong> Bank details are validated to ensure payments can be processed</li>
          <li><strong>History:</strong> All payments you've received are tracked for transparency</li>
          <li><strong>Updates:</strong> Contact an administrator to update your bank details</li>
        </ul>
      </Alert>
    </div>
  );
};

const mapStateToProps = (state: IState) => ({
  self: state.persons.self && state.persons.map[state.persons.self],
});

export default connect(mapStateToProps)(PersonalBankDetailsSettings);
